import { cn } from '@/utils';
import React, { useState } from 'react';
import wingoLogoLight from '@/assets/wingo-logo-light.svg';
import wingoIcon from '@/assets/wingo-icon.svg';

interface LogoIconProps {
  className?: string;
  /** A tenant's logo (XC-ERP1-U11); the Wingo logo when absent or when it fails to load. */
  imageUrl?: string;
  /** Alt text for ``imageUrl``; the Wingo logo is always "Wingo". */
  alt?: string;
  /** Light-badge mode: the tenant logo sits on a white pill (a dark logo on the dark header). */
  badge?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const heightClasses = {
  sm: 'h-6',
  md: 'h-8',
  lg: 'h-10',
};

// Inside the badge the image steps down one size, so the pill keeps the slot's height.
const badgedHeightClasses = {
  sm: 'h-4',
  md: 'h-6',
  lg: 'h-8',
};

export const LogoMark: React.FC<{ size: 'sm' | 'md' | 'lg'; className?: string }> = ({ size, className }) => (
  <img
    src={wingoIcon}
    alt=""
    aria-hidden="true"
    className={cn(heightClasses[size], 'w-auto flex-shrink-0', className)}
  />
);

export const LogoIcon: React.FC<LogoIconProps> = ({
  className,
  imageUrl,
  alt,
  badge = false,
  size = 'md',
}) => {
  // The URL that failed (an expired or missing signed URL): fall back to Wingo
  // rather than show a broken image. A different URL gets a fresh try.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const custom = imageUrl && imageUrl !== failedUrl ? imageUrl : undefined;

  if (!custom) {
    return (
      <img
        src={wingoLogoLight}
        alt="Wingo"
        className={cn(heightClasses[size], 'w-auto object-contain select-none', className)}
      />
    );
  }

  const img = (
    <img
      src={custom}
      alt={alt ?? 'Logo'}
      onError={() => setFailedUrl(custom)}
      className={cn(
        badge ? badgedHeightClasses[size] : heightClasses[size],
        'w-auto max-w-full object-contain select-none',
        className,
      )}
    />
  );
  if (!badge) return img;
  return (
    <span
      data-testid="logo-badge"
      className="inline-flex max-w-full items-center bg-white/95 px-2 py-1 rounded-lg"
    >
      {img}
    </span>
  );
};
